import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconButton, Tooltip, makeStyles } from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { useUsageInfo } from "./hooks/useUsageInfo";
import ProjectForm from "./components/ProjectForm";
import ProjectSummary from "./components/ProjectSummary";
import styles from "./styles.module.css";
import PageHeader from "components/layout/page-header/PageHeader";
import api from "api";

const useBackButtonStyles = makeStyles(theme => ({
  iconButton: {
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

function ProjectCreation({ token, email }) {
  const backButtonClasses = useBackButtonStyles();
  const navigate = useNavigate();
  const {
    maxStorageInBytes,
    usedStorageInBytes,
    maxProjects,
    currentProjects,
    isLoading,
    error,
    incrementProjectCount
  } = useUsageInfo(token);

  const [existingProjects, setExistingProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  // Ref to call setValue on the form from outside
  const formSetValueRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects/user", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const raw = res.data;
        if (Array.isArray(raw)) {
          setExistingProjects(raw);
        }
      } catch (err) {
        console.error("Erro ao buscar projetos existentes:", err);
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();
  }, [token]);

  const existingProjectNames = existingProjects.map(p => p.name);

  const handleSelectProject = useCallback(name => {
    if (formSetValueRef.current) {
      formSetValueRef.current("name", name, { shouldValidate: true });
    }
  }, []);

  const handleSubmissionSuccess = useCallback(() => {
    incrementProjectCount();
    // Refresh project list
    if (!token) return;
    api.get("/projects/user", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (Array.isArray(res.data)) setExistingProjects(res.data);
    }).catch(() => {});
  }, [token, incrementProjectCount]);

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        title="Criar ou adicionar ao projeto"
        subtitle="Envie arquivos para criar um projeto novo ou adicionar mais produtos a um projeto que já existe (mesmo nome)."
        accent="Projetos"
        leftAdornment={
          <Tooltip title="Voltar">
            <IconButton
              aria-label="Voltar"
              onClick={() => navigate(-1)}
              size="small"
              className={backButtonClasses.iconButton}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <main className={styles.mainContent}>
        <div className={`${styles.card} ${styles.formContainer}`}>
          <ProjectForm
            token={token}
            email={email}
            storageData={{ maxStorageInBytes, usedStorageInBytes }}
            onDataChange={() => {}}
            currentProjectCountInDB={currentProjects}
            maxProjectLimit={maxProjects}
            onSubmissionSuccess={handleSubmissionSuccess}
            existingProjectNames={existingProjectNames}
            setValueRef={formSetValueRef}
          />
        </div>
        <aside className={styles.summaryContainer}>
          <ProjectSummary
            usedStorageInBytes={usedStorageInBytes}
            maxStorageInBytes={maxStorageInBytes}
            currentProjects={currentProjects}
            maxProjects={maxProjects}
            isLoading={isLoading || projectsLoading}
            existingProjects={existingProjects}
            onSelectProject={handleSelectProject}
          />
          {error && <p className="text-center text-danger mt-3">{error}</p>}
        </aside>
      </main>
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth.accessToken,
  email: state.auth.email
});
export default connect(mapStateToProps)(ProjectCreation);
